import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Lets a customer swap ONE "Tapis N" color property on a bundle cart line
 * (X2/X3 Premium - CleanyStep) without returning to the product page - the
 * other tapis on the same line keep their own color property untouched.
 *
 * Near-identical twin of cart-line-color-swap.js (same popover
 * positioning fix, same add-then-remove safety guarantee, same
 * resolve-with-full-cart follow-up - see that file's doc comment for the
 * full history of bugs already found and fixed in this exact mechanism).
 * The one real difference: there is no different variant to add here - the
 * SAME variant id is re-added, only the "Tapis N" key inside `properties`
 * changes, every other property (the other tapis' colors) is carried over
 * unchanged from data-properties (set by cart-line-tapis-color-swap.liquid
 * from the line's real current properties).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} status - Hidden live region for success/error messaging.
 *
 * @extends {Component<Refs>}
 */
export class CartLineTapisColorSwapComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Meme positionnement "a la main" que cart-line-color-swap.js, pour la
    // meme raison (voir son propre commentaire) : la position anchor CSS
    // native, sans polyfill JS pour les navigateurs qui ne la supportent
    // pas, s'est deja averee cassante et difficile a verifier sans navigateur
    // reel.
    const popover = this.querySelector('[popover]');
    const trigger = this.querySelector('[popovertarget]');
    if (popover instanceof HTMLElement && trigger instanceof HTMLElement) {
      popover.addEventListener('beforetoggle', (event) => {
        if (!(event instanceof ToggleEvent) || event.newState !== 'open') return;
        this.#positionPopover(popover, trigger);
      });
    }
  }

  /**
   * @param {HTMLElement} popover
   * @param {HTMLElement} trigger
   */
  #positionPopover(popover, trigger) {
    const spacing = 6;
    const viewportMargin = 16;
    const maxWidth = 220;

    const rect = trigger.getBoundingClientRect();
    const left = Math.max(viewportMargin, Math.min(rect.left, window.innerWidth - maxWidth - viewportMargin));

    popover.style.position = 'fixed';
    popover.style.top = `${rect.bottom + spacing}px`;
    popover.style.left = `${left}px`;
  }

  /**
   * Handles a click on one of the alternative color options.
   * @param {string} encodedColor - The new color's name, percent-encoded (cart-line-tapis-color-swap.liquid's `url_param_escape` - color names can contain spaces/accents, e.g. "Gris Foncé", not safe raw in an on:click path segment).
   * @param {PointerEvent} event
   */
  selectColor(encodedColor, event) {
    event.preventDefault();

    if (this.hasAttribute('data-loading')) return;

    const { lineKey, quantity, variantId, tapisIndex } = this.dataset;
    if (!lineKey || !quantity || !variantId || !tapisIndex) return;

    /** @type {Record<string, string>} */
    let properties = {};
    try {
      properties = JSON.parse(this.dataset.properties || '{}');
    } catch (error) {
      console.warn('[cart-line-tapis-color-swap] data-properties parse failed, continuing without it', error);
    }
    properties[`Tapis ${tapisIndex}`] = decodeURIComponent(encodedColor);

    // Not this.refs.popover - the popover belongs to the nested
    // anchored-popover-component's own ref scope, not this one's - same
    // reasoning as cart-line-color-swap.js.
    if (event.target instanceof Element) {
      const popover = event.target.closest('[popover]');
      if (popover instanceof HTMLElement) popover.hidePopover();
    }
    this.setAttribute('data-loading', '');
    this.#setStatus('');

    const addPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: Number(quantity) }],
        promise: addPromise.promise,
      })
    );

    fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: variantId, quantity: Number(quantity), properties }],
          sections: this.#cartSectionIds().join(','),
        }),
      })
    )
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          addPromise.reject(new Error(response.message || 'Add to cart failed'));
          this.#setStatus(response.message || 'Impossible de changer cette couleur.');
          return null;
        }

        // Fire-and-forget, never awaited before #removeLine() - see
        // cart-line-color-swap.js's class doc for why.
        this.#resolveWithFullCart(addPromise, response);

        return this.#removeLine(lineKey);
      })
      .catch((error) => {
        console.error('[cart-line-tapis-color-swap]', error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error while swapping color',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        this.#setStatus('Une erreur réseau est survenue, merci de réessayer.');
      })
      .finally(() => {
        this.removeAttribute('data-loading');
      });
  }

  /**
   * Removes the original line now that the replacement (same variant,
   * updated properties) has been added. Mirrors cart-line-color-swap.js's
   * own #removeLine().
   * @param {string} lineKey
   * @returns {Promise<void>}
   */
  #removeLine(lineKey) {
    const removePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: lineKey, quantity: 0 }],
        promise: removePromise.promise,
      })
    );

    return fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', {
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: this.#cartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      })
    )
      .then((response) => response.json())
      .then((response) => {
        if (response.errors) {
          const message = typeof response.errors === 'string' ? response.errors : 'Cart update failed';
          removePromise.reject(new Error(message));
          this.#setStatus(
            'La nouvelle couleur a été ajoutée, mais l’ancienne ligne n’a pas pu être retirée automatiquement. Merci de la supprimer manuellement.'
          );
          return;
        }

        this.#resolveWithFullCart(removePromise, response);
      })
      .catch((error) => {
        console.error('[cart-line-tapis-color-swap] remove failed after add succeeded', error);
        removePromise.reject(error);
        this.#setStatus(
          'La nouvelle couleur a été ajoutée, mais l’ancienne ligne n’a pas pu être retirée automatiquement. Merci de la supprimer manuellement.'
        );
      });
  }

  /**
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} promiseHandle
   * @param {{sections?: unknown, items?: unknown}} addOrChangeResponse
   */
  async #resolveWithFullCart(promiseHandle, addOrChangeResponse) {
    let cart = null;
    try {
      const fullCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart);
    } catch (error) {
      console.warn('[cart-line-tapis-color-swap] full cart fetch/conversion failed, continuing anyway', error);
    }

    promiseHandle.resolve({
      cart,
      detail: {
        sections: addOrChangeResponse.sections,
        items: addOrChangeResponse.items,
        source: 'cart-line-tapis-color-swap',
        didError: false,
      },
    });
  }

  /** @returns {string[]} Section IDs of every cart-items-component on the page. */
  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(/** @returns {id is string} */ (id) => Boolean(id));
  }

  /** @param {string} message */
  #setStatus(message) {
    const { status } = this.refs;
    if (status) status.textContent = message;
  }
}

if (!customElements.get('cart-line-tapis-color-swap-component')) {
  customElements.define('cart-line-tapis-color-swap-component', CartLineTapisColorSwapComponent);
}
